'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    { label: 'Search Flights', href: '/flight-search', icon: 'MagnifyingGlassIcon' },
    { label: 'Hotels', href: '/hotels', icon: 'BuildingOffice2Icon' },
    { label: 'Holidays', href: '/holidays', icon: 'GlobeAltIcon' },
    { label: 'Track PNR', href: '/live-pnr-dashboard', icon: 'MapPinIcon' },
    { label: 'My Bookings', href: '/booking-management', icon: 'TicketIcon' },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header suppressHydrationWarning className={`bg-white/95 backdrop-blur-sm shadow-sm border-b border-border/50 fixed top-0 left-0 right-0 z-50 ${className}`}>
      <div className="w-full max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-20 px-4 lg:px-8">
          {/* Logo */}
          <Link 
            href="/homepage" 
            className="flex items-center gap-3 group"
            onClick={closeMobileMenu}
          >
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L20 8V16L12 22L4 16V8L12 2Z" fill="#FF6B35" />
                  <path d="M12 7L16 10V14L12 17L8 14V10L12 7Z" fill="white" />
                </svg>
              </div>
            </div>
            <div className="flex flex-col min-w-0">
              <span className="font-heading font-black text-sm sm:text-base md:text-lg text-primary leading-tight group-hover:text-secondary transition-colors break-words max-w-[140px] sm:max-w-none">
                DIAL FOR FLIGHT
              </span>
              <span className="text-[10px] sm:text-xs text-muted-foreground font-bold leading-none mt-1 tracking-wide whitespace-nowrap">
                ✈️ AI-Powered Travel
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-2">
            <Link
              href="/flight-search"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="MagnifyingGlassIcon" size={18} className="text-current" />
              <span>Flights</span>
            </Link>
            <Link
              href="/hotels"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="BuildingOffice2Icon" size={18} className="text-current" />
              <span>Hotels</span>
            </Link>
            <Link
              href="/holidays"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="GlobeAltIcon" size={18} className="text-current" />
              <span>Holidays</span>
            </Link>
            <Link
              href="/innovation-showcase"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="SparklesIcon" size={18} className="text-current" />
              <span>Innovation</span>
            </Link>
            <Link
              href="/lead-dashboard"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="ChartBarIcon" size={18} className="text-current" />
              <span>Leads</span>
            </Link>
            <Link
              href="/live-pnr-dashboard"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="MapPinIcon" size={18} className="text-current" />
              <span>Track PNR</span>
            </Link>
            <Link
              href="/booking-management"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="TicketIcon" size={18} className="text-current" />
              <span>My Bookings</span>
            </Link>
            <Link
              href="/onward-ticket"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="DocumentCheckIcon" size={18} className="text-current" />
              <span>Onward Ticket</span>
            </Link>
            <Link
              href="/visa-requirements-matrix"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="TableCellsIcon" size={18} className="text-current" />
              <span>Visa Matrix</span>
            </Link>
            <Link
              href="/marketplace"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="ShoppingBagIcon" size={18} className="text-current" />
              <span>Marketplace</span>
            </Link>
            <Link
              href="/visa-tracking"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="ClipboardDocumentCheckIcon" size={18} className="text-current" />
              <span>Visa Tracking</span>
            </Link>
            <Link
              href="/itinerary-gallery"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="GlobeAltIcon" size={18} className="text-current" />
              <span>Destinations</span>
            </Link>
            <Link
              href="/trust-center"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="ShieldCheckIcon" size={18} className="text-current" />
              <span>Trust Center</span>
            </Link>
            <Link
              href="/contact-support"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="ChatBubbleLeftRightIcon" size={18} className="text-current" />
              <span>Contact</span>
            </Link>
            <Link
              href="/user-dashboard"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="UserCircleIcon" size={18} className="text-current" />
              <span>Dashboard</span>
            </Link>
            <Link
              href="/commission-dashboard"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="CurrencyPoundIcon" size={18} className="text-current" />
              <span>Commissions</span>
            </Link>
            <Link
              href="/partner-agency"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="BuildingOffice2Icon" size={18} className="text-current" />
              <span>Partners</span>
            </Link>
            <Link
              href="/support"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="LifebuoyIcon" size={18} className="text-current" />
              <span>Support</span>
            </Link>
            <Link
              href="/analytics"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="ChartBarIcon" size={18} className="text-current" />
              <span>Analytics</span>
            </Link>
            <Link
              href="/affiliate"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="LinkIcon" size={18} className="text-current" />
              <span>Affiliates</span>
            </Link>
            <Link
              href="/feedback"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="StarIcon" size={18} className="text-current" />
              <span>Feedback</span>
            </Link>
            <Link
              href="/system-health"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="SignalIcon" size={18} className="text-current" />
              <span>System Health</span>
            </Link>
            <Link
              href="/data-backups"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="CircleStackIcon" size={18} className="text-current" />
              <span>Backups</span>
            </Link>
            <Link
              href="/performance-analytics"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="BoltIcon" size={18} className="text-current" />
              <span>Performance</span>
            </Link>
            <Link
              href="/notification-center"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="BellAlertIcon" size={18} className="text-current" />
              <span>Notifications</span>
            </Link>
            <Link
              href="/audit-logs"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="ClipboardDocumentListIcon" size={18} className="text-current" />
              <span>Audit Logs</span>
            </Link>
            <Link
              href="/blog-articles"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="NewspaperIcon" size={18} className="text-current" />
              <span>Blog</span>
            </Link>
          </nav>

          {/* CTA Buttons - Desktop */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              href="/admin"
              className="relative p-2.5 rounded-lg text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="Cog6ToothIcon" size={22} />
            </Link>
            <Link
              href="/notification-center"
              className="relative p-2.5 rounded-lg text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="BellAlertIcon" size={22} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-coral rounded-full animate-pulse" />
            </Link>
            <Link
              href="/booking"
              className="px-6 py-2.5 bg-gradient-to-r from-accent to-[#FF8C5A] text-white text-sm font-heading font-black rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
            >
              Book Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            suppressHydrationWarning
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-lg text-foreground hover:bg-surface/80 transition-colors duration-200"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={26} />
          </button>
        </div>

        {/* Mobile Navigation */}
        <div
          suppressHydrationWarning
          className={`lg:hidden bg-white border-t border-border/50 shadow-lg overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-screen animate-slide-in' : 'max-h-0'
          }`}
        >
          <nav className="px-4 py-6 space-y-2" suppressHydrationWarning>
            <Link
              href="/flight-search"
              onClick={closeMobileMenu}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="MagnifyingGlassIcon" size={22} className="text-current" />
              <span>Flights</span>
            </Link>
            <Link
              href="/hotels"
              onClick={closeMobileMenu}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="BuildingOffice2Icon" size={22} className="text-current" />
              <span>Hotels</span>
            </Link>
            <Link
              href="/holidays"
              onClick={closeMobileMenu}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="GlobeAltIcon" size={22} className="text-current" />
              <span>Holidays</span>
            </Link>
            <Link
              href="/lead-dashboard"
              onClick={closeMobileMenu}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground hover:bg-primary/5 hover:text-primary transition-all duration-200"
            >
              <Icon name="ChartBarIcon" size={20} className="text-current" />
              <span className="font-bold">Lead Dashboard</span>
            </Link>
            <Link
              href="/live-pnr-dashboard"
              onClick={closeMobileMenu}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground hover:bg-primary/5 hover:text-primary transition-all duration-200"
            >
              <Icon name="MapPinIcon" size={20} className="text-current" />
              <span className="font-bold">Track PNR</span>
            </Link>
            <Link
              href="/booking-management"
              onClick={closeMobileMenu}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="TicketIcon" size={22} className="text-current" />
              <span>My Bookings</span>
            </Link>
            <Link
              href="/onward-ticket"
              onClick={closeMobileMenu}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="DocumentCheckIcon" size={22} className="text-current" />
              <span>Onward Ticket</span>
            </Link>
            <Link
              href="/user-dashboard"
              onClick={closeMobileMenu}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="UserCircleIcon" size={22} className="text-current" />
              <span>Dashboard</span>
            </Link>
            <Link
              href="/contact-support"
              onClick={closeMobileMenu}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-bold text-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200"
            >
              <Icon name="ChatBubbleLeftRightIcon" size={22} className="text-current" />
              <span>Contact</span>
            </Link>
            <Link
              href="/notification-center"
              onClick={closeMobileMenu}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground hover:bg-primary/5 hover:text-primary transition-all duration-200"
            >
              <Icon name="BellAlertIcon" size={20} className="text-current" />
              <span className="font-bold">Notifications</span>
            </Link>
            <Link
              href="/audit-logs"
              onClick={closeMobileMenu}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground hover:bg-primary/5 hover:text-primary transition-all duration-200"
            >
              <Icon name="ClipboardDocumentListIcon" size={20} className="text-current" />
              <span className="font-bold">Audit Logs</span>
            </Link>
            <Link
              href="/booking"
              onClick={closeMobileMenu}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-black text-white bg-gradient-to-r from-accent to-[#FF8C5A] hover:shadow-lg transition-all duration-200"
            >
              <Icon name="TicketIcon" size={22} className="text-current" />
              <span>Book Now</span>
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;